import type React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  BaseInputEditor,
  type BaseInputEditorVisibilityProps,
} from "@/components/common/BaseInputEditor";
import type { User } from "@/lib/humhub-api/types";
import { cn } from "@/lib/utils";

interface BaseComposeInputProps extends BaseInputEditorVisibilityProps {
  placeholder?: string;
  submitButtonLabel?: string;
  showFormatting?: boolean;
  onSubmit?: (content: string, files?: File[]) => Promise<void>;
  className?: string;
  disabled?: boolean;
  maxFileSize: number;
  maxLength: number;
  allowedFileTypes?: string[];
  mentionUsers?: User[];
  isLoadingMentionUsers?: boolean;
  onMentionTrigger?: (query: string) => Promise<User[]>;
  autoFocus?: boolean;
  compact?: boolean;
  searchLikeWhenBlurred?: boolean;
  context?: "post" | "chat";
}

export const BaseComposeInput: React.FC<BaseComposeInputProps> = ({
  placeholder,
  submitButtonLabel,
  showFormatting = true,
  onSubmit,
  className = "",
  disabled,
  maxFileSize,
  maxLength,
  allowedFileTypes,
  mentionUsers = [],
  isLoadingMentionUsers = false,
  onMentionTrigger,
  autoFocus = false,
  compact = false,
  searchLikeWhenBlurred = false,
  context = "post",
  alwaysVisible,
}) => {
  const { t } = useTranslation();
  const [isEditorFocused, setIsEditorFocused] = useState(false);
  const isSearchLikeBlurred = searchLikeWhenBlurred && !isEditorFocused;

  const containerClassName = cn(
    "w-full rounded-tr-lg border bg-gray-50 transition-all duration-200 shadow-sm [&_.base-input-editor]:leading-6",
    isSearchLikeBlurred
      ? "h-auto !border-0 !bg-transparent px-0 py-0 shadow-none"
      : compact
        ? "border-gray-100 p-2"
        : "border-gray-100 p-3",
  );

  const editorClassName = cn(
    "base-input-editor",
    isSearchLikeBlurred && "min-h-0 leading-5",
    className,
  );

  return (
    <div className={containerClassName}>
      <BaseInputEditor
        placeholder={placeholder || t("common.inputPlaceholder")}
        submitButtonLabel={submitButtonLabel || t("common.create")}
        showFormatting={showFormatting}
        onSubmit={onSubmit}
        className={editorClassName}
        disabled={disabled}
        maxFileSize={maxFileSize}
        allowedFileTypes={allowedFileTypes}
        onMentionTrigger={onMentionTrigger}
        mentionUsers={mentionUsers}
        isLoadingMentionUsers={isLoadingMentionUsers}
        context={context}
        maxLength={maxLength}
        autoFocus={autoFocus}
        onFocusChange={setIsEditorFocused}
        alwaysVisible={alwaysVisible}
      />
    </div>
  );
};
